import { FC, ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AuthPageWrapper,
  FieldProps,
  PageSpinner,
  PasswordField,
} from '@app/components';
import { sharedMessages } from '@app/translations';

import '@app/components/AuthPageWrapper/AuthPageWrapper.scss';

export interface FieldDescription {
  label: ReactElement;
  name: string;
  component: FC<FieldProps>;
}

interface AuthFormProps {
  fields: FieldDescription[];
  additionalFields?: ReactNode;
  passwordResetLink?: ReactNode;
  isLoading?: boolean;
  shouldDisplayFields?: boolean;
  onSubmit: (values, form) => void;
}

const cnBem = createBemBlockBuilder(['authPageWrapper']);

export const AuthForm: FC<AuthFormProps> = ({
  fields,
  additionalFields,
  passwordResetLink,
  isLoading,
  shouldDisplayFields = true,
  onSubmit,
}) => {
  const { $t } = useIntl();
  const form = useForm({ mode: 'onTouched' });
  const {
    handleSubmit: hookFormHandleSubmit,
    control,
    formState: { isSubmitting, isValid, isDirty },
  } = form;

  const handleSubmit = (values: FormData) => onSubmit(values, form);

  return (
    <AuthPageWrapper.Form onSubmit={hookFormHandleSubmit(handleSubmit)}>
      {shouldDisplayFields && (
        <>
          {isLoading && <PageSpinner className={cnBem('__spinner')} />}
          {fields.map(fieldDescription => (
            <AuthPageWrapper.Row key={fieldDescription.name}>
              <fieldDescription.component
                rules={{ required: $t(sharedMessages.required) }}
                name={fieldDescription.name}
                label={fieldDescription.label}
                control={control}
              />
              {fieldDescription.component === PasswordField &&
                passwordResetLink}
            </AuthPageWrapper.Row>
          ))}
          <AuthPageWrapper.Row>
            <AuthPageWrapper.SubmitButton
              isDisabled={!isValid || isSubmitting || !isDirty}
            >
              {$t(sharedMessages.signIn)}
            </AuthPageWrapper.SubmitButton>
          </AuthPageWrapper.Row>
        </>
      )}
      {additionalFields}
    </AuthPageWrapper.Form>
  );
};
